/*
 * The StackSchematic product family, as data.
 *
 * ## Why this file states a status per product
 *
 * The obvious build is eight sidebar entries leading to eight dashboards. That would be a lie in
 * five places. CLAUDE.md's status section is explicit that Catalog, Graph, Live, Blueprint and
 * Connect are design rather than code, and the coding principles forbid "stub services, fake
 * data, or example components that imply functionality that does not exist".
 *
 * So the family is presented in full — a person should be able to see what StackSchematic is
 * *for* without reading the architecture document — and each product says plainly whether it has
 * a surface, and what that surface is. A product with nothing built says so rather than opening
 * an empty page that looks broken.
 *
 * The honest version is also the more useful one: "Live will own OBSERVED and PROBED evidence,
 * and is not built" tells a reader something. An empty dashboard tells them the software is
 * faulty.
 */

/** Whether there is anything to open. */
export type ProductStatus = "available" | "partial" | "design";

export interface Product {
  readonly name: string;
  /** What it is for, in one line a person outside the project would understand. */
  readonly purpose: string;
  /** Which evidence class it owns. The reason the family is split this way at all. */
  readonly evidence: string;
  readonly status: ProductStatus;
  /**
   * Where its surface is, when it has one.
   *
   * `internal` paths are routes inside this viewer. `external` ones are separately served
   * origins — the Guide and Recover single-page apps, which are their own builds rather than
   * pages of this one.
   */
  readonly surface:
    | { readonly kind: "internal"; readonly path: string; readonly label: string }
    | { readonly kind: "external"; readonly configKey: string; readonly label: string }
    | { readonly kind: "none"; readonly why: string };
  /**
   * What "available" does not cover, when that is worth saying on the card.
   *
   * A product can be built and reachable while the evidence behind it is thin — Live is
   * exactly that: the surface is real and passive observation is not running continuously.
   * Marking it `available` without saying so would make the badge a claim about the estate
   * rather than about the software, which is the same laundering the evidence model exists
   * to prevent, one level up.
   */
  readonly caveat?: string;
}

export const PRODUCTS: readonly Product[] = Object.freeze([
  {
    name: "Catalog",
    purpose: "The application catalog — applications, ownership, declared metadata.",
    evidence: "DECLARED",
    status: "available",
    surface: {
      kind: "internal",
      /*
       * Backstage's own catalog, not a page of ours.
       *
       * There was one — `/declared`, listing the claims rather than the entities — and it was
       * removed. Every active declaration carries the whole act as annotations on the entity
       * itself (`declared-by`, `declared-at`, `declared-reason`, `promoted-from`), so the page
       * re-listed, in a worse table, data Backstage already renders with search, filters,
       * relations, entity pages and TechDocs attached. A product owning an evidence class does
       * not oblige us to build it a surface when a better one exists.
       */
      path: "/catalog",
      label: "Open the catalog",
    },
    caveat:
      "Its surface is Backstage's own catalog: every declared entity carries who declared it, " +
      "when, why, and the evidence it was promoted from, as annotations. What is NOT there is " +
      "the append-only history — retracted and superseded claims never become entities — which " +
      "is readable through GET /admin/declared and has no page of its own.",
  },
  {
    name: "Graph",
    purpose:
      "The technology graph — entities, relationships, provenance and confidence. The only " +
      "place evidence classes meet, which is what makes 'never silently promote an inference' " +
      "enforceable rather than aspirational.",
    evidence: "consumes all; produces INFERRED",
    status: "available",
    surface: {
      kind: "internal",
      /*
       * Backstage's catalog graph, which is an actual graph.
       *
       * Ours was a page listing every inference the software had drawn. It was not a graph, it
       * read as a second catalog, and worse: it put the caveat on a page a reader had to go and
       * correlate by hand, while the conclusion itself was shown plainly on Discovery without
       * it. The inference now travels ON the row it qualifies, which serves rule 1 better —
       * "never SILENTLY promote" is about the place a conclusion is used, not about whether a
       * list of conclusions exists somewhere.
       */
      path: "/catalog-graph",
      label: "Open the graph",
    },
    caveat:
      "Relationships are drawn by Backstage from the catalog. What StackSchematic INFERS — a " +
      "role read from a port convention — is marked on the thing it qualifies, in Discovery, " +
      "rather than collected on a page of its own. No inference can be promoted from any " +
      "surface: that takes an attributable human act through the declaration path.",
  },
  {
    name: "Discovery",
    purpose:
      "Automated discovery of infrastructure, identity, cloud, Kubernetes, repositories and " +
      "on-premises assets, from a fleet of vantage-point agents.",
    evidence: "DISCOVERED",
    status: "available",
    surface: {
      kind: "internal",
      // /discovery, not /agents. The Agents page is the fleet's own surface — the instruments,
      // their scope, and whether they can look at all. This is the estate they found, and
      // conflating the two would undo the distinction that keeps agents out of the catalog.
      path: "/discovery",
      label: "Open Discovery",
    },
    caveat:
      "The hosts, services and storage the fleet found, with where each was seen from. " +
      "Cartography's cloud and identity discovery is not wired into this view — it is run by " +
      "hand from the runbook. The agents themselves are managed on the Agents page.",
  },
  {
    name: "Live",
    purpose:
      "Runtime telemetry and status — what has actually been witnessed at runtime, as opposed " +
      "to what exists.",
    evidence: "OBSERVED, PROBED",
    status: "available",
    surface: {
      kind: "internal",
      /*
       * Discovery, not a page of its own — and the label says so rather than saying "Open Live"
       * and landing you somewhere else.
       *
       * There WAS a page, with sections for OBSERVED and PROBED. Nearly every discovered socket
       * is also probed, so its one populated section listed rows already on Discovery in a
       * different order, while the finding actually worth having was visible only by reading
       * the two side by side: discovered, and never witnessed in use. That is now a count in
       * Discovery's headline and a marker on every row.
       *
       * `available` still means openable — the invariant the Apps page tests hold — because it
       * is. What changed is that two products share one surface, which is honest: they are two
       * evidence classes about the same things.
       */
      path: "/discovery",
      label: "See it in Discovery",
    },
    caveat:
      "Passive observation is not running continuously and almost nothing is instrumented, so " +
      "most things have never been witnessed in use. That is a fact about our looking rather " +
      "than about the estate, and Discovery says so beside the count.",
  },
  {
    name: "Blueprint",
    purpose: "The visual, application-centric technology blueprint. Reads and never writes.",
    evidence: "derived views",
    status: "available",
    surface: {
      kind: "internal",
      path: "/blueprint",
      label: "Open Blueprint",
    },
    caveat:
      "Each application with the evidence its dependencies rest on, and what nobody has " +
      "established. It draws no edge the catalog does not already have — correlating a " +
      "declared thing with an observed one is a claim about identity, and this view makes none.",
  },
  {
    name: "Connect",
    purpose:
      "Attaching, configuring and monitoring the evidence sources — the plumbing that keeps " +
      "reconciliation from having to know about it.",
    evidence: "—",
    status: "available",
    surface: {
      kind: "internal",
      path: "/connect",
      label: "Open Connect",
    },
    caveat:
      "Monitors the evidence sources the fleet reports — what is attached, what is " +
      "delivering, and what is attached but unable to look. Attaching and configuring a " +
      "source is still done by hand from the runbooks.",
  },
  {
    name: "Recover",
    purpose:
      "ABARMS — recovery documentation, assessment and planning. What must come back, in what " +
      "order, and what nobody has answered. Documents and plans; it automates nothing.",
    evidence: "DECLARED profiles; INFERRED plans",
    status: "available",
    surface: {
      kind: "internal",
      // Recover is a separately-served SPA; RecoverPage frames it at /recover so the viewer's
      // nav stays around it, rather than opening a bare new window that leaves the nav behind.
      path: "/recover",
      label: "Open Recover",
    },
  },
  {
    name: "Guide",
    purpose:
      "The optional AI experience over the whole family — reads every product through the MCP " +
      "boundary and asserts nothing of its own.",
    evidence: "reads all; asserts none",
    status: "available",
    surface: {
      kind: "none",
      /*
       * The floating launcher, on every page — including this one.
       *
       * There WAS a framed page at /guide, added because the Apps card used to open the
       * standalone chat in a new browser window and leave the viewer's nav behind. Framing it
       * fixed the window, and left two ways into one conversation.
       *
       * The dock is the better of the two and not only because it came first: Guide is a
       * chatbot, and a chatbot that follows you keeps the thing you were looking at. A page you
       * navigate TO costs you the context you wanted to ask about, which is the one thing the
       * dock's `postMessage` context bridge exists to preserve.
       */
      why:
        "No page: the launcher is on every page in the viewer, including this one. Guide is a " +
        "chatbot, and one that follows you keeps whatever you were looking at — navigating to " +
        "it would cost you the context you wanted to ask about.",
    },
    caveat:
      "Optional, and off entirely when the deployment says so. It reads the other products " +
      "through MCP and asserts nothing of its own.",
  },
]);

export const STATUS_MEANING: Readonly<Record<ProductStatus, string>> = Object.freeze({
  available: "Built, with its own surface.",
  partial: "Something real exists, but not the product as designed.",
  design: "Designed and documented. Not built.",
});
